/**
 * Relaxes href-based selectors to use "ends-with" matching.
 * Handles cases where selectors were recorded with full paths (e.g., /p/0/interfaces/.../homepage)
 * but the rendered elements have shorter paths (e.g., /homepage).
 */

const CSS_HREF_EXACT_RE = /\[href=["']([^"']+)["']\]/;
const XPATH_HREF_EXACT_RE = /@href='([^']+)'/;

/**
 * Extract the last meaningful path segment from an href.
 * e.g., '/p/0/interfaces/test/preview/homepage' -> '/homepage'
 * Returns null if the href has only one segment (no relaxation possible).
 */
function getLastPathSegment(href: string): string | null {
  const cleaned = href.replace(/\\\//g, '/');
  const segments = cleaned.split('/').filter(Boolean);

  if (segments.length <= 1) return null;

  return '/' + segments[segments.length - 1];
}

/**
 * Creates a relaxed CSS selector by converting exact href match to ends-with ($=).
 * Returns null if the selector has no href attribute or doesn't need relaxation.
 */
export function relaxCssHrefSelector(selector: string): string | null {
  const match = CSS_HREF_EXACT_RE.exec(selector);

  if (!match) return null;

  const lastSegment = getLastPathSegment(match[1]);

  if (!lastSegment) return null;

  return selector.replace(CSS_HREF_EXACT_RE, `[href$="${lastSegment}"]`);
}

/**
 * Creates a relaxed XPath selector by converting exact href match to contains().
 * Returns null if the xpath has no href attribute or doesn't need relaxation.
 */
export function relaxXPathHrefSelector(xpath: string): string | null {
  const match = XPATH_HREF_EXACT_RE.exec(xpath);

  if (!match) return null;

  const lastSegment = getLastPathSegment(match[1]);

  if (!lastSegment) return null;

  return xpath.replace(XPATH_HREF_EXACT_RE, `contains(@href, '${lastSegment}')`);
}
